<script setup lang="ts">
import { computed } from 'vue';

import { useGlobalCurrencyStore } from '@apps/main/new-desktop/core/stores';

import { AppSimpleTable, AppStandardModal } from '@libs/ui';

import type { PriceBreakdownModalData, StayPriceBreakdownData } from './models';
import {
  getCancellationPoliciesRows,
  getPriceBreakdownRows,
} from './PriceBreakdownModal.helper';
import { getPriceBreakdownModalData } from './priceBreakdownCalculator';

interface PriceBreakdownModalProps {
  data: StayPriceBreakdownData;
}

const props = defineProps<PriceBreakdownModalProps>();

interface PriceBreakdownModalEmits {
  (eventName: 'close'): void;
}

const emit = defineEmits<PriceBreakdownModalEmits>();

const globalCurrencyStore = useGlobalCurrencyStore();

const modalData = computed<PriceBreakdownModalData>(() =>
  getPriceBreakdownModalData(props.data)
);
</script>

<template>
  <AppStandardModal
    :close-button-aria-label="$t('a11y.close_panel')"
    @close="emit('close')"
  >
    <template #title>
      {{ $t('planner.more_information') }}
    </template>

    <template #body>
      <div class="price-breakdown-modal-body">
        <template v-if="modalData.forRooms.length > 1">
          <div class="title">{{ $t('taxes_and_fees.summary') }}</div>
          <div
            v-if="modalData.summary.priceBreakdown.taxesAndFees.length"
            class="price-breakdown"
          >
            <AppSimpleTable
              :header="$t('planner.price_breakdown')"
              :rows="
                getPriceBreakdownRows(
                  modalData.summary.priceBreakdown,
                  globalCurrencyStore.formatMoney
                )
              "
            />
          </div>

          <div class="cancellation-policies">
            <AppSimpleTable
              :header="$t('planner.cancellation')"
              :rows="
                getCancellationPoliciesRows(
                  modalData.summary.cancellationPolicies,
                  globalCurrencyStore.formatMoney
                )
              "
            />
          </div>
        </template>

        <div
          v-for="(room, index) of modalData.forRooms"
          :key="index"
          class="room-details"
        >
          <div class="title">{{ $t('planner.pax.rooms') }} {{ index + 1 }}</div>
          <div class="room-name">{{ room.roomName }}</div>

          <AppSimpleTable
            v-if="room.roomSummary.priceBreakdown.taxesAndFees.length"
            :header="$t('planner.price_breakdown')"
            :rows="
              getPriceBreakdownRows(
                room.roomSummary.priceBreakdown,
                globalCurrencyStore.formatMoney
              )
            "
            class="room-price-breakdown"
          />

          <AppSimpleTable
            :header="$t('planner.cancellation')"
            :rows="
              getCancellationPoliciesRows(
                room.roomSummary.cancellationPolicies,
                globalCurrencyStore.formatMoney
              )
            "
          />
        </div>
      </div>
    </template>
  </AppStandardModal>
</template>

<style scoped lang="scss">
.price-breakdown-modal-body {
  .title {
    @include font-label-l-bold;
    color: var(--color-neutral-70);
    margin-bottom: var(--spacing-16);
  }

  .room-name {
    @include font-body-m-light;
    color: var(--color-neutral-70);
    margin-bottom: var(--spacing-16);
  }

  .price-breakdown,
  .cancellation-policies,
  .room-details {
    margin-bottom: var(--spacing-32);
  }

  .room-price-breakdown {
    margin-bottom: var(--spacing-16);
  }
}
</style>
